/**
 * Today's Attendance Widget — real-time clock-in / late / absent / on leave snapshot.
 * For HR/Director dashboard quick-glance.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Clock, CheckCircle, AlertCircle, UserX, Plane } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatItem = ({ label, value, icon: Icon, color }) => (
  <div className="text-center">
    <div className={`mx-auto w-10 h-10 rounded-full ${color} flex items-center justify-center mb-1`}>
      <Icon className="w-5 h-5 text-white" />
    </div>
    <p className="text-2xl font-bold text-gray-800">{value}</p>
    <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">{label}</p>
  </div>
);

const TodayAttendanceWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const { data } = await axios.get(`${API}/dashboard/today-attendance`);
        if (!cancelled) setData(data);
      } catch { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    };
    fetchData();
    const t = setInterval(fetchData, 60_000); // refresh every 60s
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (loading || !data) return null;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-5" data-testid="today-attendance-widget">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2">
          <Clock className="w-4 h-4 text-red-700" />
          Kehadiran Hari Ini
          <span className="text-[10px] font-medium text-gray-400 normal-case">{data.date}</span>
        </h3>
        <span className="text-xs text-gray-500">
          {data.total_staff} staff aktif
        </span>
      </div>
      <div className="grid grid-cols-4 gap-2 sm:gap-3">
        <StatItem label="On Time" value={data.on_time} icon={CheckCircle} color="bg-emerald-500" />
        <StatItem label="Late" value={data.late} icon={AlertCircle} color="bg-amber-500" />
        <StatItem label="Cuti" value={data.on_leave} icon={Plane} color="bg-blue-500" />
        <StatItem label="Absent" value={data.absent} icon={UserX} color="bg-red-500" />
      </div>
      {data.on_leave_staff?.length > 0 && (
        <div className="mt-4 pt-3 border-t border-gray-100">
          <p className="text-[10px] font-bold tracking-wider text-gray-400 uppercase mb-1.5">Cuti Hari Ini</p>
          <div className="flex flex-wrap gap-1.5">
            {data.on_leave_staff.slice(0, 6).map((s, i) => (
              <span key={i} className="text-[11px] px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full">
                {s.name}
              </span>
            ))}
            {data.on_leave_staff.length > 6 && (
              <span className="text-[11px] px-2 py-0.5 text-gray-500">+{data.on_leave_staff.length - 6} lagi</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TodayAttendanceWidget;
